import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuthStore } from '@/stores/authStore';

export interface ActivityLog {
  id: string;
  workspaceId: string;
  userId: string;
  action: string;
  entityType: string;
  entityId?: string;
  entityName?: string;
  metadata: Record<string, unknown>;
  createdAt: string;
  userEmail?: string;
}

function mapDbToActivityLog(db: Record<string, unknown>): ActivityLog {
  return {
    id: db.id as string,
    workspaceId: db.workspace_id as string,
    userId: db.user_id as string,
    action: db.action as string,
    entityType: db.entity_type as string,
    entityId: db.entity_id as string | undefined,
    entityName: db.entity_name as string | undefined,
    metadata: (db.metadata as Record<string, unknown>) || {},
    createdAt: db.created_at as string,
  };
}

export function useActivityLogs(workspaceId: string | undefined, filters?: {
  userId?: string;
  entityType?: string;
  action?: string;
  startDate?: string;
  endDate?: string;
  limit?: number;
}) {
  return useQuery({
    queryKey: ['activity-logs', workspaceId, filters],
    queryFn: async () => {
      if (!workspaceId) return [];

      let query = supabase
        .from('activity_logs')
        .select('*')
        .eq('workspace_id', workspaceId)
        .order('created_at', { ascending: false });

      if (filters?.userId) {
        query = query.eq('user_id', filters.userId);
      }
      if (filters?.entityType) {
        query = query.eq('entity_type', filters.entityType);
      }
      if (filters?.action) {
        query = query.eq('action', filters.action);
      }
      if (filters?.startDate) {
        query = query.gte('created_at', filters.startDate);
      }
      if (filters?.endDate) {
        query = query.lte('created_at', filters.endDate);
      }
      if (filters?.limit) {
        query = query.limit(filters.limit);
      } else {
        query = query.limit(100);
      }

      const { data, error } = await query;

      if (error) throw error;
      return (data || []).map(d => mapDbToActivityLog(d as Record<string, unknown>));
    },
    enabled: !!workspaceId,
  });
}

export function useLogActivity() {
  const queryClient = useQueryClient();
  const { user } = useAuthStore();

  return useMutation({
    mutationFn: async ({
      workspaceId,
      action,
      entityType,
      entityId,
      entityName,
      metadata,
    }: {
      workspaceId: string;
      action: string;
      entityType: string;
      entityId?: string;
      entityName?: string;
      metadata?: Record<string, unknown>;
    }) => {
      if (!user) throw new Error('Not authenticated');

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { error } = await (supabase
        .from('activity_logs') as any)
        .insert({
          workspace_id: workspaceId,
          user_id: user.id,
          action,
          entity_type: entityType,
          entity_id: entityId || null,
          entity_name: entityName || null,
          metadata: metadata || {},
        });

      if (error) throw error;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['activity-logs', variables.workspaceId] });
    },
  });
}

export const ACTION_LABELS: Record<string, string> = {
  created: 'Created',
  updated: 'Updated',
  deleted: 'Deleted',
  approved: 'Approved',
  rejected: 'Rejected',
  submitted: 'Submitted for approval',
  assigned: 'Assigned',
  commented: 'Commented on',
  invited: 'Invited',
  joined: 'Joined',
  left: 'Left',
  role_changed: 'Changed role',
};

export const ENTITY_LABELS: Record<string, string> = {
  qr_code: 'QR Code',
  folder: 'Folder',
  member: 'Member',
  invitation: 'Invitation',
  workspace: 'Workspace',
  comment: 'Comment',
};
